/**
 * ExportCompensationConfig - NEM 3.0 Export Compensation Configuration
 *
 * Allows users to configure export compensation for solar/storage systems.
 * Supports:
 *   - Hourly NEM 3.0 rates (PG&E, SCE, SDG&E)
 *   - Match import rate (NEM 2.0 style)
 *   - Fixed rate
 *   - No export (grid_export_limit_kw: 0)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Alert,
    CircularProgress,
    Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxColors, API_BASE_URL, useAuth } from '@vortx/shared-ui';

// Export mode options
const EXPORT_MODES = {
    hourly: {
        label: 'Hourly NEM 3.0',
        description: 'Time-varying export rates from utility',
    },
    match_import: {
        label: 'Match Import Rate',
        description: 'Export rate follows your import TOU schedule',
    },
    fixed: {
        label: 'Fixed Rate',
        description: 'Single rate for all exported energy',
    },
    none: {
        label: 'No Export',
        description: 'No export compensation - all generation must be self-consumed',
    },
};

// Utility code to display name mapping
const UTILITY_NAMES = {
    PGE: 'PG&E',
    SCE: 'SCE',
    SDGE: 'SDG&E',
};

/**
 * ExportCompensationConfig Component
 *
 * @param {Object} props
 * @param {string} props.utilityCode - Utility code (PGE, SCE, SDGE)
 * @param {Object} props.value - Current export config { mode, rate, tariffId, tariffData }
 * @param {Function} props.onChange - Callback when config changes
 * @param {boolean} props.disabled - Whether to disable the component
 */
function ExportCompensationConfig({
    utilityCode,
    value = {},
    onChange,
    disabled = false,
}) {
    const { currentUser } = useAuth();

    // Local state
    const [availableTariffs, setAvailableTariffs] = useState([]);
    const [utilityMapping, setUtilityMapping] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Destructure value with defaults
    const {
        mode = 'none',
        rate = '',
        tariffId = null,
        tariffData = null,
        enabled = false,
    } = value;

    // Determine if hourly rates are available for this utility
    const hasHourlyRates = utilityCode && utilityMapping[utilityCode];
    const defaultTariffId = utilityMapping[utilityCode] || null;

    // Fetch available export tariffs on mount
    useEffect(() => {
        const fetchTariffs = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/export-tariffs`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!response.ok) {
                    throw new Error(`Failed to fetch export tariffs: ${response.status}`);
                }
                const data = await response.json();
                setAvailableTariffs(data.tariffs || []);
                setUtilityMapping(data.utility_mapping || {});
            } catch (err) {
                console.error('Error fetching export tariffs:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTariffs();
    }, []);

    // Auto-select hourly tariff when utility changes and hourly rates are available
    useEffect(() => {
        if (enabled && hasHourlyRates && !tariffId && mode === 'hourly') {
            // Fetch the tariff data for auto-selected tariff
            fetchTariffData(defaultTariffId);
        }
    }, [utilityCode, enabled, hasHourlyRates, mode]);

    // Fetch full tariff data including 8760 rates
    // baseState parameter allows passing the current state to avoid stale closure issues
    const fetchTariffData = useCallback(async (id, baseState = null) => {
        if (!id) return;

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/export-tariffs/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch tariff: ${response.status}`);
            }
            const data = await response.json();

            // Use baseState if provided, otherwise fall back to current value
            const currentState = baseState || value;
            onChange({
                ...currentState,
                tariffId: id,
                tariffData: data,
            });
        } catch (err) {
            console.error('Error fetching export tariff data:', err);
            setError(err.message);
        }
    }, [onChange, value]);

    // Handle enabled toggle
    const handleEnabledChange = (event) => {
        const newEnabled = event.target.checked;

        if (newEnabled) {
            // Auto-detect mode based on utility
            const newMode = hasHourlyRates ? 'hourly' : 'match_import';
            const newTariffId = newMode === 'hourly' ? defaultTariffId : null;

            const newState = {
                enabled: true,
                mode: newMode,
                rate: '',
                tariffId: newTariffId,
                tariffData: null,
            };
            onChange(newState);

            // Fetch tariff data if hourly mode - pass newState to avoid stale closure
            if (newMode === 'hourly' && newTariffId) {
                fetchTariffData(newTariffId, newState);
            }
        } else {
            onChange({
                enabled: false,
                mode: 'none',
                rate: '',
                tariffId: null,
                tariffData: null,
            });
        }
    };

    // Handle mode change
    const handleModeChange = (event) => {
        const newMode = event.target.value;

        if (newMode === 'hourly') {
            const newTariffId = defaultTariffId;
            const newState = {
                ...value,
                mode: newMode,
                tariffId: newTariffId,
                tariffData: null,
            };
            onChange(newState);
            if (newTariffId) {
                fetchTariffData(newTariffId, newState);
            }
        } else {
            onChange({
                ...value,
                mode: newMode,
                tariffId: null,
                tariffData: null,
            });
        }
    };

    // Handle tariff selection change
    const handleTariffChange = (event) => {
        const newTariffId = event.target.value;
        const newState = {
            ...value,
            tariffId: newTariffId,
            tariffData: null,
        };
        onChange(newState);
        fetchTariffData(newTariffId, newState);
    };

    // Handle fixed rate change
    const handleRateChange = (event) => {
        onChange({
            ...value,
            rate: event.target.value,
        });
    };

    // Get available export modes based on utility
    const getAvailableModes = () => {
        const modes = [];

        if (hasHourlyRates) {
            modes.push({
                value: 'hourly',
                label: `Hourly NEM 3.0 (${UTILITY_NAMES[utilityCode] || utilityCode})`,
            });
        }

        modes.push({ value: 'match_import', label: 'Match Import Rate (NEM 2.0 style)' });
        modes.push({ value: 'fixed', label: 'Fixed Rate...' });
        modes.push({ value: 'none', label: 'None (no export allowed)' });

        return modes;
    };

    // Render preview info based on mode
    const renderPreviewInfo = () => {
        if (!enabled) return null;

        if (mode === 'hourly' && tariffData?.statistics) {
            const stats = tariffData.statistics;
            return (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        label={`Avg: $${stats.avg?.toFixed(3) || '0.00'}/kWh`}
                        sx={{ bgcolor: alpha(vortxColors.gold, 0.1) }}
                    />
                    <Chip
                        size="small"
                        label={`Peak: $${stats.max?.toFixed(2) || '0.00'}/kWh`}
                        sx={{ bgcolor: alpha(vortxColors.gold, 0.2) }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        (Aug-Sep evenings)
                    </Typography>
                </Box>
            );
        }

        if (mode === 'match_import') {
            return (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Export rate follows your import TOU schedule
                </Typography>
            );
        }

        if (mode === 'fixed' && rate) {
            return (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Fixed export rate: ${parseFloat(rate).toFixed(3)}/kWh
                </Typography>
            );
        }

        if (mode === 'none') {
            return (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    No export compensation - all generation must be self-consumed
                </Typography>
            );
        }

        return null;
    };

    // Show loading state
    if (loading) {
        return (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                    Loading export tariffs...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            mb: 3,
            p: 2,
            bgcolor: alpha(vortxColors.gold, 0.05),
            borderRadius: 1,
            border: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
        }}>
            <Typography
                variant="subtitle2"
                sx={{ color: vortxColors.gold, mb: 1.5 }}
            >
                Export Compensation
            </Typography>

            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Enable/Disable Toggle */}
            <FormControlLabel
                control={
                    <Checkbox
                        checked={enabled}
                        onChange={handleEnabledChange}
                        disabled={disabled}
                        sx={{
                            color: alpha(vortxColors.gold, 0.6),
                            '&.Mui-checked': { color: vortxColors.gold },
                        }}
                    />
                }
                label={
                    <Typography variant="body2">
                        Include export compensation
                        {!hasHourlyRates && utilityCode && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                            >
                                (NEM 3.0 hourly rates not available for {utilityCode})
                            </Typography>
                        )}
                    </Typography>
                }
            />

            {/* Export Mode Configuration (shown when enabled) */}
            {enabled && (
                <Box sx={{ mt: 2, pl: 4 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                            Export Type
                        </Typography>
                        <Select
                            value={mode}
                            onChange={handleModeChange}
                            disabled={disabled}
                        >
                            {getAvailableModes().map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Tariff selector (shown for hourly mode with multiple options) */}
                    {mode === 'hourly' && availableTariffs.length > 1 && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                Export Tariff
                            </Typography>
                            <Select
                                value={tariffId || ''}
                                onChange={handleTariffChange}
                                disabled={disabled}
                            >
                                {availableTariffs
                                    .filter(t => !utilityCode || t.utility_code === utilityCode)
                                    .map((tariff) => (
                                        <MenuItem key={tariff.id} value={tariff.id}>
                                            {tariff.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Fixed rate input (shown for fixed mode) */}
                    {mode === 'fixed' && (
                        <TextField
                            label="Export Rate"
                            type="number"
                            size="small"
                            value={rate}
                            onChange={handleRateChange}
                            disabled={disabled}
                            InputProps={{
                                startAdornment: (
                                    <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>
                                ),
                                endAdornment: (
                                    <Typography variant="caption" sx={{ ml: 0.5 }}>/kWh</Typography>
                                ),
                            }}
                            inputProps={{ step: 0.001, min: 0 }}
                            sx={{ width: 180 }}
                        />
                    )}

                    {/* Preview Info */}
                    {renderPreviewInfo()}
                </Box>
            )}
        </Box>
    );
}

export default ExportCompensationConfig;
